// src/utils/date.js
// Shared utility functions for date and time formatting

import { format, parseISO, isValid, formatDistanceToNow } from 'date-fns';

/**
 * Formats a date string into 'yyyy-MM-dd' format.
 * Returns 'N/A' or 'Invalid Date' on error.
 * @param {string | Date | null | undefined} dateValue - The date value to format.
 * @returns {string} The formatted date string.
 */
export const formatDate = (dateValue) => {
 if (!dateValue) return 'N/A';
 try {
  const date = dateValue instanceof Date ? dateValue : parseISO(dateValue);
  if (!isValid(date)) return 'Invalid Date';
  return format(date, 'yyyy-MM-dd');
 } catch (e) {
  console.error("Error formatting date:", dateValue, e);
  return 'Invalid Date';
 }
};

/**
 * Formats a date/time string into locale-specific date and time.
 * Returns 'N/A' or 'Invalid Date' on error.
 * @param {string | Date | null | undefined} dateValue - The date/time value to format.
 * @returns {string} The formatted date/time string.
 */
export const formatDateTime = (dateValue) => {
 if (!dateValue) return 'N/A';
 try {
  const date = dateValue instanceof Date ? dateValue : parseISO(dateValue);
  if (!isValid(date)) return 'Invalid Date';
  return date.toLocaleString(); // Use browser's locale settings
 } catch (e) {
  console.error("Error formatting date/time:", dateValue, e);
  return 'Invalid Date';
 }
};

/**
 * Formats a date string into a human-readable distance from now (e.g., "3 days ago").
 * Returns 'unknown time' or 'invalid date' on error.
 * @param {string | Date | null | undefined} dateValue - The date value to format.
 * @returns {string} The formatted distance string.
 */
export const formatTimeAgo = (dateValue) => {
  try {
    if (!dateValue) return "unknown time";
    const date = dateValue instanceof Date ? dateValue : parseISO(dateValue);
    if (!isValid(date)) {
      console.warn("Error parsing date for time ago:", dateValue);
      return "invalid date";
    }
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    console.error("Error formatting time ago:", dateValue, e);
    return "invalid date";
  }
};

// Note: You might need to install date-fns if you haven't already: npm install date-fns
